/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
/** @jsx jsx */
import { jsx } from "@emotion/core"
import { FC, Fragment, useCallback, useEffect } from "react"
import { Drawer } from "antd"
import { Form } from "grab-ui"
import { useTranslationsMap } from "common/utils/i18n"
import { MenuGroupLinkStoreDrawer } from "components/drawers/MenuGroupLinkStoreDrawer"
import { useDispatch, useSelector } from "react-redux"
import { AppState } from "store/store.type"
import { actionCreator } from "store/store"
import { waitMenuGroupPropagete } from "utils/menu_group"
import { AnyAction } from "redux"
import { ResponseWithError } from "types/api"
import notification from "components/common/notification"
import {
  getResponseErrorEventLabels,
  getResponseErrorEventLabelsForRTK,
  TroyWebTracker,
} from "utils/tracking"
import {
  EventAction,
  EventCategory,
  EventActionEng,
  EventCategoryEng,
} from "constants/tracking"
import useRtkQueryMigrationEndpointsFlag from "hooks/useRtkQueryMigrationEndpointsFlag"
import {
  useLazyFetchMenuGroupDetailQuery,
  useUnlinkStoreFromMenuGroupMutation,
} from "queries/menuApi"
import { fetchMenuGroupDetailWithMigration } from "utils/rtk_migration_helper"
import useFetchAllMenuGroupLinkedStoreList from "hooks/useFetchAllMenuGroupLinkedStoreList"
import useFetchAllStoreList from "hooks/useFetchAllStoreList"
import { useHistory } from "react-router"
import { MENUS } from "constants/routes"
import { ActionList } from "./ActionList"
import {
  menuGroupLinkedStoreDrawerItemAction,
  menuGroupLinkedStoreDrawerListAction,
  menuGroupLinkedStoreDrawerStyle,
} from "./styles"
import { drawerTitleStyle } from "../styles"

export interface MenuGroupLinkedStoreDrawerProps extends FormComponentProps {
  visible: boolean
  onClose: () => void
}

export const MenuGroupLinkedStoreDrawerComponent: FC<MenuGroupLinkedStoreDrawerProps> = ({
  visible,
  onClose,
}: MenuGroupLinkedStoreDrawerProps) => {
  const {
    menu: {
      drawer,
      menuGroupDetail: { menuGroupID, latestPropagateStatus },
      merchantGroupID,
      menuGroupLinkedStoresIDs = [],
      outlets,
      stopAfterFirstPage,
      outletsHasMore,
    },
  } = useSelector((state: AppState) => state)

  const { showMenuGroupLinkStore } = drawer

  const linkedStores = menuGroupLinkedStoresIDs
    .map(storeID => {
      const outlet = outlets?.find(o => o.merchantID === storeID)
      return outlet
        ? { label: outlet.merchantName, value: outlet.merchantID }
        : null
    })
    .filter(Boolean) as { label: string; value: string }[]

  const dispatch = useDispatch()
  const history = useHistory()
  const fetchAllMenuGroupLinkedStoreList = useFetchAllMenuGroupLinkedStoreList()

  const {
    flags: {
      unlinkStoreFromMenuGroup: isRtkUnlinkStoreFromMenuGroupEnabled,
      fetchMenuGroupDetail: isRtkFetchMenuGroupDetailEnabled,
    },
    isLoading: isRtkFlagLoading,
  } = useRtkQueryMigrationEndpointsFlag()

  const [unlinkStoreFromMenuGroupRtk] = useUnlinkStoreFromMenuGroupMutation()
  const [fetchMenuGroupDetailRtk] = useLazyFetchMenuGroupDetailQuery()

  const textMap = useTranslationsMap({
    title: "menu.menuGroup.linkedStore.drawer.title",
    unlink: "menu.menuGroup.linkedStore.drawer.unlink",
    addStore: "menu.menuGroup.linkedStore.drawer.addStore",
    menuGroupNameLabel: "menu.menuGroup.drawer.labels.name",
    save: "common.save",
    success: "common.success",
    generalError: "common.error",
    unlinkErrorMessageMFC: "menu.menuGroup.message.unlinkMFC",
  })

  // only show unlink button for non propagating menu group
  // and for normal menu (menuGroupID != storeID)
  const showUnlinkButton =
    latestPropagateStatus === 1 && (menuGroupID ?? "").includes("MENU")

  useEffect(() => {
    if (visible) {
      TroyWebTracker.sendEvent({
        category: EventCategory.menuGroupEditStores,
        action: EventAction.pageView,
        labels: {
          MERCHANT_GROUP_ID: merchantGroupID,
          MENU_GROUP_ID: menuGroupID,
        },
      })
    }
  }, [visible])

  const unlinkErrorCallback = useCallback(
    (response?: ResponseWithError) => {
      const { message = "" } = response ?? {}
      if (message.includes("MFC")) {
        return textMap.unlinkErrorMessageMFC
      }
      return true
    },
    [textMap.unlinkErrorMessageMFC],
  )

  const refreshAfterUnlink = async (updatedMenuGroupID: string) => {
    const didPropagate = await waitMenuGroupPropagete({
      menuGroupID,
      merchantGroupID,
      dispatch,
      isRtkEnabled: isRtkFetchMenuGroupDetailEnabled,
      triggerFetchMenuGroupDetailRtk: fetchMenuGroupDetailRtk,
    })
    if (!didPropagate) return false

    if (updatedMenuGroupID !== menuGroupID) {
      const fetchResult = await fetchMenuGroupDetailWithMigration({
        dispatch,
        fetchMenuGroupDetailRtk,
        isRtkFetchMenuGroupDetailEnabled,
        request: {
          menuGroupID: updatedMenuGroupID,
          merchantGroupID,
        },
      })
      if (!fetchResult?.type || fetchResult.type.endsWith("_FAIL")) return false
      history.replace(`${MENUS}/${updatedMenuGroupID}`)
    } else {
      fetchAllMenuGroupLinkedStoreList()
      notification.success({ message: textMap.success })
    }
    return true
  }

  const handleClickItemAction = useCallback(
    async (storeID: string) => {
      if (!storeID) {
        return
      }
      const t0 = performance.now()
      dispatch(actionCreator.global.showLoading())

      let labels = {
        MERCHANT_GROUP_ID: merchantGroupID,
        MENU_GROUP_ID: menuGroupID,
        STORE_ID_SELECTED: storeID,
        LOADING_STATUS: "SUCCESS",
        LOAD_TIME: 0,
        REASON: "",
        ERROR_MSG: "",
      }

      if (isRtkUnlinkStoreFromMenuGroupEnabled) {
        // RTK Query path
        const result = await unlinkStoreFromMenuGroupRtk({
          merchantGroupID,
          menuGroupID,
          storeIDs: [storeID],
        })

        if (result.error) {
          const error = result.error as any
          const errorData = error?.data as ResponseWithError | undefined
          const errorMessage = unlinkErrorCallback(errorData)
          if (errorMessage && typeof errorMessage === "string") {
            notification.error({ message: errorMessage })
          } else if (
            error?.status >= 400 &&
            error?.status < 500 &&
            errorData?.message
          ) {
            notification.error({ message: errorData.message })
          } else {
            notification.error({
              message: textMap.generalError || "common.error",
            })
          }
          labels = {
            ...labels,
            LOADING_STATUS: "FAILURE",
            ...getResponseErrorEventLabelsForRTK(result),
          }
        } else if (result.data) {
          const updatedMenuGroupID = result.data.menuGroupID || menuGroupID
          if (!(await refreshAfterUnlink(updatedMenuGroupID))) {
            labels = { ...labels, LOADING_STATUS: "FAILURE" }
          }
        }

        TroyWebTracker.sendEvent({
          category: EventCategoryEng.rtkMigration,
          action: EventActionEng.unlinkStoreFromMenuGroup,
          labels: {
            MERCHANT_ID: merchantGroupID,
            IS_RTK_ENABLED: isRtkUnlinkStoreFromMenuGroupEnabled,
            FROM_RTK_QUERY: true,
            FROM_OLD_REDUX: false,
            ACTION_NAME: "unlinkStoreFromMenuGroup",
          },
        })
      } else {
        // Old Redux path
        const resp: any = await dispatch(
          actionCreator.menu.unlinkStoreFromMenuGroup({
            merchantGroupID,
            menuGroupID,
            storeIDs: [storeID],
            errorCallback: unlinkErrorCallback,
          }) as AnyAction,
        )

        if (resp?.type?.endsWith("_SUCCESS")) {
          const updatedMenuGroupID =
            resp?.payload?.data?.menuGroupID || menuGroupID
          if (!(await refreshAfterUnlink(updatedMenuGroupID))) {
            labels = { ...labels, LOADING_STATUS: "FAILURE" }
          }
        } else if (resp?.type?.endsWith("_FAIL")) {
          labels = {
            ...labels,
            LOADING_STATUS: "FAILURE",
            ...getResponseErrorEventLabels(resp),
          }
        }

        TroyWebTracker.sendEvent({
          category: EventCategoryEng.rtkMigration,
          action: EventActionEng.unlinkStoreFromMenuGroup,
          labels: {
            MERCHANT_ID: merchantGroupID,
            IS_RTK_ENABLED: isRtkUnlinkStoreFromMenuGroupEnabled,
            FROM_RTK_QUERY: false,
            FROM_OLD_REDUX: true,
            ACTION_NAME: "unlinkStoreFromMenuGroup",
          },
        })
      }

      const t1 = performance.now()
      labels = {
        ...labels,
        LOAD_TIME: t1 - t0,
      }
      TroyWebTracker.sendEvent({
        category: EventCategory.menuGroupEditStores,
        action: EventAction.clickUnlink,
        labels,
      })
      dispatch(actionCreator.global.hideLoading())
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      dispatch,
      menuGroupID,
      merchantGroupID,
      unlinkErrorCallback,
      isRtkUnlinkStoreFromMenuGroupEnabled,
      isRtkFetchMenuGroupDetailEnabled,
      unlinkStoreFromMenuGroupRtk,
      fetchMenuGroupDetailRtk,
      textMap.generalError,
    ],
  )

  const fetchAllStoreList = useFetchAllStoreList()

  useEffect(() => {
    if (stopAfterFirstPage && outletsHasMore) {
      fetchAllStoreList({
        disableGlobalLoading: true,
      })
    }
  }, [fetchAllStoreList, stopAfterFirstPage, outletsHasMore])

  return (
    <Fragment>
      <Drawer
        title={<span css={drawerTitleStyle}>{textMap.title}</span>}
        placement="right"
        width={450}
        visible={visible}
        onClose={onClose}
        destroyOnClose
        css={menuGroupLinkedStoreDrawerStyle}
      >
        <ActionList
          data-testid="action-list"
          items={linkedStores}
          itemAction={
            showUnlinkButton &&
            !isRtkFlagLoading && (
              <a
                css={menuGroupLinkedStoreDrawerItemAction}
                className="itemActionContainer"
              >
                {textMap.unlink}
              </a>
            )
          }
          listAction={
            <a css={menuGroupLinkedStoreDrawerListAction}>
              {textMap.addStore.toLocaleLowerCase()}
            </a>
          }
          onClickListAction={() => {
            TroyWebTracker.sendEvent({
              category: EventCategory.menuGroupEditStores,
              action: EventAction.clickLinkNewStores,
              labels: {
                MERCHANT_GROUP_ID: merchantGroupID,
                MENU_GROUP_ID: menuGroupID,
              },
            })
            dispatch(
              actionCreator.menu.toggleDrawer({
                showMenuGroupLinkStore: true,
              }),
            )
          }}
          onClickItemAction={
            isRtkFlagLoading
              ? undefined
              : (storeID: string) => handleClickItemAction(storeID)
          }
        />
      </Drawer>
      <MenuGroupLinkStoreDrawer
        data-testid="link-store-drawer"
        visible={!!showMenuGroupLinkStore}
        onClose={() => {
          dispatch(
            actionCreator.menu.toggleDrawer({
              showMenuGroupLinkStore: false,
            }),
          )
        }}
      />
    </Fragment>
  )
}
export const MenuGroupLinkedStoreDrawer = Form.create<
  MenuGroupLinkedStoreDrawerProps
>({})(MenuGroupLinkedStoreDrawerComponent)
